/** Console v0.5: Super admin (design brief v0.5 s.6-7, the SuperAdmin mock-up), on session A's routes (#378, #379;
 * contracts s.13-14). Only a super admin session reaches it, and that session can't open any customer screen or data.
 * Accounts, limits, people's names and emails, and (with B6) organisation names and ABNs: never books. Every action is
 * logged on the server. */

import { ApiError } from "../api.js";
import type { ScreenContext } from "./app.js";
import { SCREENS } from "./app.js";
import {
  changeLimits,
  createAccount,
  createPlan,
  fetchAccountOrganisations,
  fetchAccountPeople,
  fetchAccounts,
  fetchAccountsWithoutPlan,
  lookupAbn,
  resendAdminInvite,
  setSuspended,
} from "./admin-api.js";
import type { AccountWithoutPlan, AdminAccount, AdminPerson } from "./admin-api.js";
import {
  ADMIN_COPY,
  PRESETS,
  abnNameDiffers,
  abnValid,
  abnView,
  createBody,
  createProblem,
  matchesFilter,
  matchesSearch,
  planBody,
  planProblem,
  presetName,
  showNoPlanPanel,
  statusLabel,
} from "./admin-logic.js";
import type { AbnOutcome, AbnView, AccountFilter, AccountType, Preset } from "./admin-logic.js";
import { capabilityLabels, deliveryNotice, shortDate } from "./team-logic.js";
import { avatar, button, figure, h, legalFooter, loading, notice, openDialog, pageHead, statTile, table, tabs, tag, uid } from "./ui.js";
import { formatAbn, initials } from "./routes.js";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** A 401 (15 minutes idle, or 4 hours) ends the admin session: back to its sign-in, saying why. */
function expired(err: unknown): boolean {
  if (err instanceof ApiError && err.status === 401) {
    location.assign("/super-admin?signed_out=1");
    return true;
  }
  return false;
}

function statusTag(status: string): HTMLElement {
  return tag(statusLabel(status), status === "active" ? "good" : status === "pending_payment" ? "warn" : "neutral");
}

function numberField(label: string, value: number, disabled = false): { node: HTMLElement; input: HTMLInputElement } {
  const input = h("input", "pk5-input pk5-figure");
  input.type = "number";
  input.min = "1";
  input.step = "1";
  input.value = String(value);
  input.disabled = disabled;
  const id = uid("pk5-num");
  input.id = id;
  const l = h("label", "pk5-label", label);
  l.htmlFor = id;
  return { node: h("div", "pk5-field", l, input), input };
}

function textField(label: string, type: string, placeholder = ""): { node: HTMLElement; input: HTMLInputElement } {
  const input = h("input", "pk5-input");
  input.type = type;
  input.placeholder = placeholder;
  input.autocomplete = "off";
  const id = uid("pk5-text");
  input.id = id;
  const l = h("label", "pk5-label", label);
  l.htmlFor = id;
  return { node: h("div", "pk5-field", l, input), input };
}

/** BR-0037: ABN Lookup beside the create-account ABN field, on blur or Check ABN. Advice only: nothing here blocks
 * Create account. The result line is a polite live region; the "Use this name" line sits outside it so typing the
 * legal name doesn't re-announce the lookup. A reply for an ABN that has since been edited is dropped. */
function abnLookup(abn: HTMLInputElement, name: HTMLInputElement): HTMLElement {
  const result = h("div", "pk5-abn-result");
  result.setAttribute("role", "status");
  const nameLine = h("div", "pk5-abn-name");
  const check = button(ADMIN_COPY.abnCheck, "secondary", () => void run());
  let answered = ""; // the digits the shown answer is for ("" = none: a failure leaves it empty so Check ABN asks again)
  let pending = "";
  let found: Extract<AbnView, { state: "found" }> | null = null;

  const hint = () => {
    found = null;
    result.replaceChildren(h("p", "pk5-muted", ADMIN_COPY.abnHint));
    nameLine.replaceChildren();
  };
  const drawName = () => {
    nameLine.replaceChildren();
    if (!found || !abnNameDiffers(name.value, found.name)) return;
    const registered = found.name;
    const use = button(ADMIN_COPY.abnUseName, "secondary", () => {
      name.value = registered;
      drawName();
      name.focus();
    });
    use.className = "pk5-link-btn";
    nameLine.append(h("p", "pk5-muted", name.value.trim() ? `${ADMIN_COPY.abnNameDiffers} ` : null, use));
  };
  const show = (v: AbnView) => {
    if (v.state !== "found") {
      found = null;
      result.replaceChildren(h("p", v.warn ? "pk5-abn-warn" : "pk5-muted", v.text));
      nameLine.replaceChildren();
      return;
    }
    found = v;
    const facts = h(
      "p",
      "pk5-muted",
      v.entity,
      " · ",
      v.active ? "Active" : "Cancelled",
      v.statusFrom ? h("span", null, " from ", figure(v.statusFrom)) : null,
      " · ",
      v.gstRegistered ? h("span", null, "GST registered", v.gstFrom ? h("span", null, " from ", figure(v.gstFrom)) : null) : "Not registered for GST"
    );
    result.replaceChildren(
      h("p", null, "ABN Lookup: ", h("strong", null, v.name), v.abn ? h("span", null, " · ABN ", figure(formatAbn(v.abn))) : null),
      facts
    );
    if (v.cancelled) result.append(h("p", "pk5-abn-warn", v.cancelled));
    drawName();
  };

  async function run(): Promise<void> {
    const digits = abn.value.replace(/\s/g, "");
    if (!digits) return hint();
    if (digits === answered || digits === pending) return;
    if (!abnValid(digits)) {
      answered = digits;
      return show(abnView({ error: "invalid_number" }));
    }
    pending = digits;
    found = null;
    nameLine.replaceChildren();
    result.replaceChildren(h("p", "pk5-muted", ADMIN_COPY.abnChecking));
    let outcome: AbnOutcome;
    try {
      outcome = { answer: await lookupAbn(digits) };
    } catch (err) {
      if (expired(err)) return;
      outcome = err instanceof ApiError ? { error: err.code, message: err.message } : { error: null };
    }
    if (pending !== digits) return; // the ABN was edited while this was out
    pending = "";
    const v = abnView(outcome);
    if (v.state === "found" || v.state === "not_found" || v.state === "invalid") answered = digits;
    show(v);
  }

  abn.addEventListener("blur", () => void run());
  abn.addEventListener("input", () => {
    if (abn.value.replace(/\s/g, "") === answered) return;
    answered = "";
    pending = "";
    hint();
  });
  name.addEventListener("input", drawName);
  hint();
  return h("div", "pk5-abn-lookup", result, check, nameLine);
}

/** The account type, plan size and limits fields, shared by "Create an account" and "Set up a plan" so the two can't drift apart. */
function planFields(): { typeFieldset: HTMLElement; planFieldset: HTMLElement; value: () => { type: AccountType; preset: Preset; organisations: number; users: number } } {
  let type: AccountType = "practice";
  let preset: Preset = "small";
  const o = numberField("Organisations allowed", PRESETS.practice.small.organisations);
  const u = numberField("Users allowed", PRESETS.practice.small.users);
  const hint = h("p", "pk5-muted", ADMIN_COPY.practiceHint);
  const typeGroup = h("div", "pk5-choice-row");
  const planGroup = h("div", "pk5-choice-row");
  const radios = (group: HTMLElement, name: string, items: Array<{ value: string; label: string; hint?: string }>, value: string, on: (v: string) => void) => {
    group.replaceChildren();
    for (const it of items) {
      const r = h("input");
      r.type = "radio";
      r.name = name;
      r.value = it.value;
      r.checked = it.value === value;
      r.addEventListener("change", () => on(it.value));
      group.append(h("label", "pk5-choice", r, h("span", "pk5-choice-text", h("span", "pk5-choice-label", it.label), it.hint ? h("span", "pk5-muted", it.hint) : null)));
    }
  };
  const typeName = uid("pk5-type");
  const planName = uid("pk5-plan");
  const sync = () => {
    const p = PRESETS[type][preset];
    o.input.value = String(p.organisations);
    u.input.value = String(p.users);
    o.input.disabled = type === "company";
    hint.textContent = type === "practice" ? ADMIN_COPY.practiceHint : ADMIN_COPY.companyHint;
    radios(
      planGroup,
      planName,
      (Object.keys(PRESETS[type]) as Preset[]).map((k) => ({ value: k, label: PRESETS[type][k].label })),
      preset,
      (v) => {
        preset = v as Preset;
        sync();
      }
    );
  };
  radios(
    typeGroup,
    typeName,
    [
      { value: "practice", label: "Practice", hint: "Looks after many organisations" },
      { value: "company", label: "Company", hint: "One business and its team" },
    ],
    type,
    (v) => {
      type = v as AccountType;
      preset = "small";
      sync();
    }
  );
  sync();
  return {
    typeFieldset: h("fieldset", "pk5-fieldset", h("legend", "pk5-label", "Account type"), typeGroup),
    planFieldset: h("fieldset", "pk5-fieldset", h("legend", "pk5-label", "Plan and limits"), planGroup, h("div", "pk5-two", o.node, u.node), hint),
    value: () => ({ type, preset, organisations: Number(o.input.value), users: Number(u.input.value) }),
  };
}

function superAdmin(ctx: ScreenContext): () => void {
  let accounts: AdminAccount[] | null = null;
  let noPlan: AccountWithoutPlan[] = []; // BR-0034: the console's own account, when it has no plan row yet
  let error: string | null = null;
  let filter: AccountFilter = "all";
  let query = "";
  let open: string | null = null;
  const people = new Map<string, AdminPerson[] | string>(); // the list, or an error
  const orgs = new Map<string, Array<{ name: string; abn: string | null }> | null>(); // null = not available yet (B6)
  let flash: { text: string; ok: boolean; link?: string } | null = null;
  let focusFlash = false;
  let gone = false;

  async function load(): Promise<void> {
    try {
      accounts = (await fetchAccounts()).accounts;
      error = null;
    } catch (err) {
      if (expired(err)) return;
      error = messageOf(err, "Preekie couldn't load the accounts. Try again in a few minutes.");
    }
    try {
      noPlan = (await fetchAccountsWithoutPlan()).accounts;
    } catch (err) {
      if (expired(err)) return;
      noPlan = []; // the panel is an extra: the main list stands without it
    }
    draw();
  }

  async function expand(id: string): Promise<void> {
    open = open === id ? null : id;
    draw();
    if (open !== id) return;
    if (!people.has(id)) {
      try {
        people.set(id, (await fetchAccountPeople(id)).people);
      } catch (err) {
        if (expired(err)) return;
        people.set(id, messageOf(err, "Preekie couldn't load this account's people."));
      }
    }
    if (!orgs.has(id)) {
      try {
        orgs.set(id, (await fetchAccountOrganisations(id)).organisations);
      } catch (err) {
        if (expired(err)) return;
        orgs.set(id, null); // arrives with A's B6; until then the counts say it
      }
    }
    draw();
  }

  function done(text: string, ok: boolean, link?: string): void {
    flash = { text, ok, link };
    focusFlash = true;
  }

  function openLimits(a: AdminAccount): void {
    const o = numberField("Organisations allowed", a.limits.organisations.max, a.type === "company");
    const u = numberField("Users allowed", a.limits.users.max);
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    openDialog(
      `Change limits: ${a.name}`,
      [
        h("p", "pk5-muted", `In use: ${a.limits.organisations.used} organisations, ${a.limits.users.used} users. Limits can't go below what's in use.`),
        h("div", "pk5-two", o.node, u.node),
        a.type === "company" ? h("p", "pk5-muted", ADMIN_COPY.companyHint) : null,
        problem,
      ].filter((x): x is HTMLParagraphElement | HTMLDivElement => !!x),
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: "Save limits",
          variant: "primary",
          onClick: async (close, btn) => {
            const mo = Number(o.input.value);
            const mu = Number(u.input.value);
            if (!Number.isInteger(mo) || !Number.isInteger(mu) || mo < 1 || mu < 1) {
              problem.textContent = "Enter whole numbers, 1 or more.";
              problem.hidden = false;
              return;
            }
            btn.disabled = true;
            try {
              await changeLimits(a.account_id, mo, mu);
              done(`Limits for ${a.name} saved.`, true);
              close();
              await load();
            } catch (err) {
              if (expired(err)) return;
              // 409 limit_below_use, 400 invalid_limits: the server's words, dialog stays open.
              problem.textContent = messageOf(err, "The limits weren't changed. Try again in a few minutes.");
              problem.hidden = false;
              btn.disabled = false;
            }
          },
        },
      ]
    );
  }

  /** BR-0034: gives an account that predates plans its plan row, through the ordinary limits route. */
  function openSetupPlan(a: AccountWithoutPlan): void {
    const plan = planFields();
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    openDialog(
      ADMIN_COPY.noPlanTitle(a.name),
      [h("p", "pk5-muted", ADMIN_COPY.noPlanLine), plan.typeFieldset, plan.planFieldset, problem],
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: ADMIN_COPY.noPlanSave,
          variant: "primary",
          onClick: async (close, btn) => {
            const form = plan.value();
            const bad = planProblem(form);
            if (bad) {
              problem.textContent = bad;
              problem.hidden = false;
              return;
            }
            btn.disabled = true;
            try {
              await createPlan(a.account_id, planBody(form));
              done(ADMIN_COPY.noPlanSaved, true);
              close();
              await load(); // both lists: the account moves into the main one and the panel goes
            } catch (err) {
              if (expired(err)) return;
              // 400 invalid_plan / invalid_limits, 409 limit_below_use / plan_exists: the server's words; the dialog stays open.
              problem.textContent = messageOf(err, ADMIN_COPY.noPlanFailed);
              problem.hidden = false;
              btn.disabled = false;
            }
          },
        },
      ]
    );
  }

  function noPlanPanel(): HTMLElement {
    const headId = uid("pk5-noplan-h");
    const heading = h("h2", "pk5-section-title", ADMIN_COPY.noPlanHeading);
    heading.id = headId;
    const rows = noPlan.map((a) => {
      const set = button(ADMIN_COPY.noPlanButton, "primary", () => openSetupPlan(a));
      set.setAttribute("aria-label", ADMIN_COPY.noPlanAriaLabel(a.name));
      return h("div", "pk5-acct", h("span", "pk5-acct-name", avatar(initials(a.name)), h("span", "pk5-person-text", h("span", "pk5-person-name", a.name), h("span", "pk5-muted", ADMIN_COPY.noPlanLabel))), set);
    });
    const section = h("section", "pk5-card", heading, h("p", "pk5-muted", ADMIN_COPY.noPlanLine), ...rows);
    section.setAttribute("aria-labelledby", headId);
    return section;
  }

  function openResend(a: AdminAccount): void {
    const email = textField("New admin email (optional)", "email", "Leave blank to resend to the same person");
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    openDialog(
      `Resend admin invite: ${a.name}`,
      [h("p", "pk5-muted", "The waiting invite is replaced, so its old link stops working."), email.node, problem],
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: "Send invite",
          variant: "primary",
          onClick: async (close, btn) => {
            btn.disabled = true;
            try {
              const r = await resendAdminInvite(a.account_id, email.input.value.trim() || undefined);
              const d = deliveryNotice(r.delivery);
              done(r.delivery === "sent" ? "Admin invite sent." : d.text, d.ok, r.test_accept_url);
              close();
              await load();
            } catch (err) {
              if (expired(err)) return;
              problem.textContent = messageOf(err, "The admin invite wasn't sent. Try again in a few minutes.");
              problem.hidden = false;
              btn.disabled = false;
            }
          },
        },
      ]
    );
  }

  function openSuspend(a: AdminAccount): void {
    const suspending = a.plan.status === "active";
    openDialog(
      suspending ? `Suspend ${a.name}?` : `Reactivate ${a.name}?`,
      [
        h(
          "p",
          null,
          suspending
            ? "Everyone in this account loses access on their next click, until you reactivate it. Nothing in the account is deleted."
            : "Everyone in this account can sign in and work again."
        ),
      ],
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: suspending ? "Suspend account" : "Reactivate account",
          variant: suspending ? "destructive" : "primary",
          onClick: async (close, btn) => {
            btn.disabled = true;
            try {
              await setSuspended(a.account_id, suspending);
              done(suspending ? `${a.name} is suspended.` : `${a.name} is active again.`, true);
            } catch (err) {
              if (expired(err)) return;
              done(messageOf(err, "That didn't work. Try again in a few minutes."), false);
            }
            close();
            await load();
          },
        },
      ]
    );
  }

  function openCreate(): void {
    const name = textField("Legal name", "text");
    const abn = textField("ABN", "text", "11 digits");
    abn.input.inputMode = "numeric";
    const adminEmail = textField("Account admin email", "email", "name@example.com");
    const plan = planFields();
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;

    openDialog(
      ADMIN_COPY.create,
      [
        plan.typeFieldset,
        h("div", "pk5-two", abn.node, name.node),
        abnLookup(abn.input, name.input),
        adminEmail.node,
        plan.planFieldset,
        h("p", "pk5-muted", ADMIN_COPY.createFootnote),
        problem,
      ],
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: "Create account",
          variant: "primary",
          onClick: async (close, btn) => {
            const form = {
              ...plan.value(),
              name: name.input.value,
              abn: abn.input.value,
              adminEmail: adminEmail.input.value,
            };
            const bad = createProblem(form);
            if (bad) {
              problem.textContent = bad;
              problem.hidden = false;
              return;
            }
            btn.disabled = true;
            try {
              const r = await createAccount(createBody(form));
              if (!r.invite) done(r.message ?? ADMIN_COPY.createdNoInvite, false);
              else {
                const d = deliveryNotice(r.delivery);
                const text =
                  r.delivery === "sent" ? ADMIN_COPY.created : r.delivery === "skipped_test_domain" ? ADMIN_COPY.createdTestDomain : ADMIN_COPY.createdNoInvite;
                done(text, d.ok, r.test_accept_url);
              }
              close();
              open = r.account.account_id;
              await load();
            } catch (err) {
              if (expired(err)) return;
              problem.textContent = messageOf(err, "The account wasn't created. Try again in a few minutes.");
              problem.hidden = false;
              btn.disabled = false;
            }
          },
        },
      ],
      { wide: true }
    );
    name.input.focus();
  }

  function accountRow(a: AdminAccount): HTMLElement {
    const isOpen = open === a.account_id;
    const panelId = `pk5-acct-${a.account_id}`;
    const head = h(
      "button",
      `pk5-acct-head${isOpen ? " pk5-acct-open" : ""}`,
      h("span", "pk5-acct-name", avatar(initials(a.name)), h("span", "pk5-person-text", h("span", "pk5-person-name", a.name), h("span", "pk5-muted", `${a.type === "practice" ? "Practice" : "Company"} · ${presetName(String(a.plan.preset))}`))),
      h("span", "pk5-acct-stat", h("span", "pk5-muted", "Organisations"), figure(`${a.limits.organisations.used}/${a.limits.organisations.max}`)),
      h("span", "pk5-acct-stat", h("span", "pk5-muted", "Users"), figure(`${a.limits.users.used}/${a.limits.users.max}`)),
      statusTag(a.plan.status)
    );
    head.type = "button";
    head.setAttribute("aria-expanded", isOpen ? "true" : "false");
    head.setAttribute("aria-controls", panelId);
    head.addEventListener("click", () => void expand(a.account_id));
    const wrap = h("div", "pk5-acct", head);
    if (!isOpen) return wrap;

    const panel = h("div", "pk5-acct-panel");
    panel.id = panelId;
    const o = orgs.get(a.account_id);
    const orgBox = h("div", "pk5-acct-col", h("h3", "pk5-subhead", "Organisations ", figure(String(a.limits.organisations.used))));
    if (o === undefined) orgBox.append(loading());
    else if (o === null || o.length === 0)
      orgBox.append(h("p", "pk5-muted", `${a.limits.organisations.used} of ${a.limits.organisations.max} organisations in use.`));
    else for (const org of o) orgBox.append(h("p", "pk5-org-line", h("span", null, org.name), org.abn ? figure(`ABN ${org.abn}`) : null));
    const ppl = people.get(a.account_id);
    const peopleBox = h("div", "pk5-acct-col", h("h3", "pk5-subhead", "Users ", figure(String(a.limits.users.used))));
    if (ppl === undefined) peopleBox.append(loading());
    else if (typeof ppl === "string") peopleBox.append(notice(ppl, "alert"));
    else if (!ppl.length) peopleBox.append(h("p", "pk5-muted", "No users yet."));
    else
      peopleBox.append(
        table(
          `Users of ${a.name}`,
          [{ label: "Name" }, { label: "Email" }, { label: "Can do" }, { label: "Status" }],
          ppl.map((p) => [p.display_name ?? "–", p.email ?? "–", capabilityLabels(p.capabilities).join(", ") || "–", p.status === "active" ? "Active" : p.status])
        )
      );
    const actions = h("div", "pk5-detail-actions");
    const limits = button("Change limits", "secondary", () => openLimits(a));
    limits.setAttribute("aria-label", `Change limits for ${a.name}`);
    const resend = button("Resend admin invite", "secondary", () => openResend(a));
    resend.setAttribute("aria-label", `Resend admin invite for ${a.name}`);
    actions.append(limits, resend);
    if (a.plan.status !== "pending_payment") {
      const s = button(a.plan.status === "active" ? "Suspend account" : "Reactivate account", a.plan.status === "active" ? "destructive" : "secondary", () => openSuspend(a));
      s.setAttribute("aria-label", `${a.plan.status === "active" ? "Suspend" : "Reactivate"} ${a.name}`);
      actions.append(s);
    }
    const created = `${shortDate(a.created_at, ctx.timeZone)} ${new Intl.DateTimeFormat("en-AU", { year: "numeric", timeZone: ctx.timeZone }).format(new Date(a.created_at))}`;
    panel.append(h("div", "pk5-acct-cols", orgBox, peopleBox), actions, h("p", "pk5-muted", "Created ", figure(created), ` · ${ADMIN_COPY.private}`));
    wrap.append(panel);
    return wrap;
  }

  function draw(): void {
    if (gone) return;
    const parts: Node[] = [pageHead("Super admin", ADMIN_COPY.intro, [button(ADMIN_COPY.create, "primary", openCreate)])];
    if (flash) {
      const n = notice(
        h("span", null, flash.text, flash.link ? h("span", "pk5-test-link", " ", Object.assign(h("a", null, "Accept link (test only)"), { href: flash.link, target: "_blank", rel: "noopener" })) : null),
        flash.ok ? "status" : "alert"
      );
      n.tabIndex = -1;
      n.classList.add("pk5-flash");
      parts.push(n);
    }
    if (error) parts.push(notice(error, "alert"));
    else if (!accounts) parts.push(loading());
    else {
      const all = accounts;
      const stats = h(
        "section",
        "pk5-stats",
        statTile("Accounts", String(all.length)),
        statTile("Practices", String(all.filter((a) => a.type === "practice").length)),
        statTile("Companies", String(all.filter((a) => a.type === "company").length)),
        statTile("Users", String(all.reduce((n, a) => n + a.limits.users.used, 0))),
        statTile("Awaiting payment", String(all.filter((a) => a.plan.status === "pending_payment").length))
      );
      stats.setAttribute("aria-label", "Platform summary");
      const defs: Array<{ id: AccountFilter; label: string }> = [
        { id: "all", label: "All" },
        { id: "practice", label: "Practices" },
        { id: "company", label: "Companies" },
        { id: "setup", label: "Awaiting setup" },
      ];
      const search = h("input", "pk5-input pk5-search-input");
      search.type = "search";
      search.placeholder = "Account name or ABN";
      search.value = query;
      search.setAttribute("aria-label", "Search accounts");
      search.addEventListener("input", () => {
        query = search.value;
        drawList();
      });
      const list = h("div", "pk5-acct-list");
      const drawList = () => {
        const shown = all.filter((a) => matchesFilter(a, filter) && matchesSearch(a, query));
        list.replaceChildren(...(shown.length ? shown.map(accountRow) : [h("p", "pk5-muted pk5-pad", "No accounts match.")]));
      };
      drawList();
      const headId = uid("pk5-acc-h");
      const heading = h("h2", "pk5-section-title", "Accounts");
      heading.id = headId;
      const section = h(
        "section",
        "pk5-card pk5-card-flush",
        h(
          "div",
          "pk5-acct-bar",
          heading,
          tabs(
            "Filter accounts",
            defs.map((d) => ({ id: d.id, label: d.label, count: all.filter((a) => matchesFilter(a, d.id)).length })),
            filter,
            (f) => {
              filter = f;
              draw();
            }
          ),
          search
        ),
        list
      );
      section.setAttribute("aria-labelledby", headId);
      parts.push(stats);
      if (showNoPlanPanel(noPlan)) parts.push(noPlanPanel());
      parts.push(section);
    }
    parts.push(legalFooter());
    ctx.root.replaceChildren(...parts);
    if (focusFlash) {
      focusFlash = false;
      (ctx.root.querySelector(".pk5-flash") as HTMLElement | null)?.focus();
    }
  }

  draw();
  void load();
  return () => {
    gone = true;
  };
}

SCREENS["super-admin"] = superAdmin;
